import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { logoutApi } from '../api/client'
import { logoutRedirectUrl } from '../api/flows'
import { ErrorMessage } from '../components/ErrorMessage'
import { AuthLayout, AuthLoading } from '../components/AuthLayout'
import { useI18n } from '../i18n'

export default function LogoutPage() {
  const { t } = useI18n()
  const [searchParams] = useSearchParams()
  const challenge = searchParams.get('logout_challenge')
  const [error, setError] = useState(false)
  const [retry, setRetry] = useState(0)
  const pending = useRef<{ key: string; promise: Promise<string> }>()

  useEffect(() => {
    let active = true
    const key = `${challenge || ''}:${retry}`
    setError(false)
    if (pending.current?.key !== key) {
      const promise = challenge
        ? Promise.resolve(`/hydra/logout?${new URLSearchParams({ logout_challenge: challenge })}`)
        : logoutApi.getLogoutUrl().then(response => response.data.logout_url)
      pending.current = { key, promise }
    }
    pending.current.promise.then(value => {
      if (!active) return
      if (typeof value !== 'string') throw new Error('Missing logout URL')
      // The bridge chains Kratos logout and Hydra logout. Navigating the full
      // browser allows both services to expire their own session cookies.
      window.location.assign(logoutRedirectUrl(value, window.location.origin))
    }).catch(() => { if (active) setError(true) })
    return () => { active = false }
  }, [challenge, retry])

  return (
    <AuthLayout title={t('Sign out')}>
      <div className="space-y-4">
        {error ? <>
          <ErrorMessage message={t('Unable to sign you out. Please try again.')} />
          <button className="w-full btn-primary" onClick={() => setRetry(value => value + 1)}>{t('Try again')}</button>
          <Link to="/" className="w-full btn-secondary text-center">{t('Go home')}</Link>
        </> : <AuthLoading message={t('Signing you out...')} />}
      </div>
    </AuthLayout>
  )
}
